/*
 * NOTE: this has been heavily modified, but feel that the below citation
 *   is still valid, as that is what it was modeled off of
 * main collection: https://www.svgrepo.com/collection/bootstrap-ui-icons/
 * specific icon: https://www.svgrepo.com/svg/344765/exclamation-triangle 
 */
type Props = {
    size?: number | string;
    /** triangle interior */
    fillColor?: string;
    /** triangle outline */
    strokeColor?: string;
    /** exclamation mark (bar + dot) */
    markColor?: string;
    /** outline thickness (viewBox units) */
    strokeWidth?: number;
    /** how round the corners should be (viewBox units) */
    cornerRadius?: number;        // try 1.0 .. 2.0
    /** scale the exclamation mark */
    markScale?: number;           // default 0.9 (10% smaller)
    className?: string;
    style?: React.CSSProperties;
    title?: string;
};

const ExclamationTriangle: React.FC<Props> = ({
    size = 16,
    fillColor = '#fff',
    strokeColor = 'currentColor',
    markColor = 'currentColor',
    strokeWidth = 1.25,
    cornerRadius = 1.2,
    markScale = 0.9,
    className,
    style,
    title
}) => {
    // keep stroke inside the 0..16 viewBox
    const half = strokeWidth / 2;
    const apex = { x: 8, y: 2 + half };
    const right = { x: 15 - half, y: 14 - half };
    const left = { x: 1 + half, y: 14 - half };

    const roundedPolygonPath = (
        pts: { x: number; y: number }[],
        r: number
    ) => {
        const n = pts.length;
        if (n < 3) return '';
        const seg = (a: { x: number; y: number }, b: { x: number; y: number }) => {
            const dx = b.x - a.x, dy = b.y - a.y;
            const len = Math.hypot(dx, dy) || 1;
            return { ux: dx / len, uy: dy / len, len };
        };

        const parts: string[] = [];
        for (let i = 0; i < n; i++) {
            const prev = pts[(i + n - 1) % n];
            const curr = pts[i];
            const next = pts[(i + 1) % n];

            const s1 = seg(curr, prev);
            const s2 = seg(curr, next);
            const off = Math.min(r, s1.len / 2, s2.len / 2);

            const p1 = { x: curr.x + s1.ux * off, y: curr.y + s1.uy * off };
            const p2 = { x: curr.x + s2.ux * off, y: curr.y + s2.uy * off };

            if (i === 0) parts.push(`M ${p1.x} ${p1.y}`);
            else parts.push(`L ${p1.x} ${p1.y}`);
            // smooth corner with quadratic curve; control point at the corner
            parts.push(`Q ${curr.x} ${curr.y} ${p2.x} ${p2.y}`);
        }
        parts.push('Z');
        return parts.join(' ');
    };

    const triPath = roundedPolygonPath([apex, right, left], cornerRadius);

    return (
        <svg
            width={size}
            height={size}
            viewBox='0 0 16 16'
            fill='none'
            aria-hidden={title ? undefined : true}
            role={title ? 'img' : 'presentation'}
            className={className}
            style={style}
        >
            {title ? <title>{title}</title> : null}

            {/* 1) Interior (rounded) */}
            <path d={triPath} style={{ fill: fillColor }} />

            {/* 2) Outline (rounded, on top) */}
            <path
                d={triPath}
                style={{
                    fill: 'none',
                    stroke: strokeColor,
                    strokeWidth,
                    strokeLinejoin: 'round',
                    strokeLinecap: 'round'
                }}
                // vectorEffect='non-scaling-stroke' // uncomment if you want constant stroke on scaling
            />

            {/* 3) Exclamation mark (scaled ~10% smaller around center ~ (8, 9)) */}
            <g transform={`translate(8 9) scale(${markScale}) translate(-8 -9)`}>
                <path
                    d='M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z'
                    style={{ fill: markColor }}
                />
            </g>
        </svg>
    );
};

export default ExclamationTriangle;
